import React, { useCallback, useState } from 'react';

import { Scanner } from '@yudiel/react-qr-scanner';
import { NewCheckoutContainer } from '../NewCheckout/Components/Container';
import { NewCheckoutSummary } from '../NewCheckout/Components/Summary';
import { NewCheckoutTicketsHeader } from '../NewCheckout/Tickets/Header';
import { WarningMessage } from 'public/components';
import { Button, Input, Loader } from 'common/lazy';
import { inject } from 'mobx-react';
import { DateTime } from 'luxon';
import { Redirect } from 'react-router';
import { useClassName, useWindowSize } from 'common/hooks';

import './pay-for-parking.less';
import { FieldContainer } from 'public/components/FieldContainer';

function getDuration(entry) {
  const inputDate = DateTime.fromISO(entry);
  const now = DateTime.now();

  // Get the duration between now and the input date
  const diff = now.diff(inputDate, ['hours', 'minutes']);

  // Format as hh:mm, padding with zeros if needed
  return `${String(diff.hours).padStart(2, '0')}:${String(
    Math.floor(diff.minutes)
  ).padStart(2, '0')}`;
}

function toUSDCents(amount) {
  return Math.round(amount * 100);
}

function PayForParkingTickets({ parking }) {
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOption, setSelectedOption] = useState('lp');
  const [licensePlate, setLicensePlate] = useState('');
  const [rate, setRate] = useState();
  const [ticket, setTicket] = useState();
  const [ticketId, setTicketId] = useState();
  const [redirect, setRedirect] = useState();
  const classNames = useClassName('PayForParking');
  const { isMobile } = useWindowSize();

  const onScan = useCallback((ticketId) => {
    async function exec() {
      setIsSearching(true);
      setTicketId(ticketId);
      setRate(await parking.getRate(ticketId));
      setTicket(await parking.getTicketInfo(ticketId));
      setIsSearching(false);
    }

    exec();
  }, []);

  console.log('rate: ', rate);
  console.log('ticket: ', ticket);

  if (redirect) {
    return <Redirect to={redirect} push />;
  }

  const ticketFound = !!rate && !!ticket;
  return (
    <NewCheckoutContainer
      content={
        <>
          <NewCheckoutTicketsHeader title="Pay for parking" />
          <WarningMessage
            content="Please pay when returning to your car to leave.
Once paid, you will have 30 minutes to exit the parking deck before being changed again."
          />

          {!ticketFound && (
            <>
              <div className={classNames('options')}>
                <Button
                  onClick={() => setSelectedOption('lp')}
                  primary={selectedOption === 'lp'}
                  secondary={selectedOption !== 'lp'}>
                  Type license plate
                </Button>
                OR
                <Button
                  onClick={() => setSelectedOption('qr')}
                  primary={selectedOption === 'qr'}
                  secondary={selectedOption !== 'qr'}>
                  Scan qr code
                </Button>
              </div>
              <div className={classNames('selected-option')}>
                {selectedOption === 'qr' && (
                  <Scanner
                    onScan={(result) => onScan(result[0].rawValue)}
                    styles={{
                      container: {
                        width: isMobile ? '100%' : '500px',
                        height: isMobile ? 'auto' : '500px',
                        borderRadius: '12px',
                      },
                    }}
                    paused={isSearching}
                    components={{
                      finder: false,
                      torch: false,
                      tracker: false,
                      onOff: false,
                      zoom: false,
                    }}>
                    {isSearching && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          background: 'white',
                          opacity: 0.6,
                          width: '100%',
                          height: '100%',
                        }}>
                        <Loader active>Get information...</Loader>
                      </div>
                    )}
                  </Scanner>
                )}
                {selectedOption === 'lp' && (
                  <div className={classNames('lp')}>
                    <FieldContainer
                      style={{
                        marginBottom: '12px',
                      }}>
                      <Input
                        id="licensePlate"
                        name="licensePlate"
                        type="licensePlate"
                        placeholder="ABC-1234"
                        onChange={(_, { value }) => setLicensePlate(value)}
                        value={licensePlate}
                      />
                    </FieldContainer>
                    <Button onClick={console.log} primary>
                      Confirm
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
          {ticketFound && (
            <div className={classNames('ticket-found')}>
              <div>Parking duration: {getDuration(ticket.EntryDateTime)}</div>
              <div>
                Time of entry:{' '}
                {DateTime.fromISO(ticket.EntryDateTime).toFormat(
                  'M/d/yy, h:mm a'
                )}
              </div>
              <div>Ticket number: {ticketId}</div>
            </div>
          )}
        </>
      }
      summary={
        <NewCheckoutSummary
          canSubmit={ticketFound}
          onSubmit={() =>
            setRedirect(`/pay-for-parking/payment?ticket_id=${ticketId}`)
          }
          step={1}
          hideAddons
          totals={{
            subtotal: toUSDCents(rate?.NetPrice?.Amount || 0),
            tax: toUSDCents(rate?.Price?.Amount - rate?.NetPrice?.Amount || 0),
            ticketCount: rate ? 1 : 0,
            ticketSubtotal: toUSDCents(rate?.NetPrice?.Amount || 0),
            total: toUSDCents(rate?.Price?.Amount || 0),
            fee: 0,
          }}
        />
      }
    />
  );
}

export default inject('parking')(PayForParkingTickets);
